/*
 * Role area (audit Phase 3.3). Replaces the six legacy wwwroot/js/Role/*.js
 * files with one module on the Phase 3.1 core. No jQuery.
 *
 * Shape of the screen: /Role renders an Application picker above a
 * master/detail split -- the role list on the left, Role/Detail on the right.
 * Detail's tab strip (Membership / Privileges / History) loads the
 * ApplicationRole and AccessChange partials, so role member and claim
 * management has exactly one implementation (Application.Role in
 * areas/application.ts) whichever screen opened it.
 *
 * Two of the six files went rather than moved:
 *   Role/Claim.js  (117 lines) -- Role.Claim.*, the row-cloning editor for
 *                  Views/Role/Claims.cshtml, deleted in the s6 dead-view
 *                  sweep. No markup has referenced it since.
 *   Role/Person.js (40 lines)  -- Role.Person.NewClick, likewise the editor
 *                  for Views/Role/People.cshtml and AddMember.cshtml, both
 *                  deleted. The only remaining hits are stale obj/*.g.cs.
 * Role/Common.js's Role.LoadTab went too: it fetched Role/{tab}Detail/{id},
 * routes the controller removed, keyed off #RoleIdField, an id only the
 * (now deleted) standalone Role/Edit.cshtml ever rendered.
 */
import { ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, one } from '../core/dom';
import { serialize } from '../core/forms';
import { request } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';
import { Role as ApplicationRole } from './application';

function value(id: string): string {
    return (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
}

function setDisplay(id: string, shown: boolean): void {
    const node = document.getElementById(id);
    if (node) node.style.display = shown ? '' : 'none';
}

/** The role the open detail pane belongs to: Detail.cshtml renders it as #Id. */
function selectedRoleId(): string {
    return value('Id');
}

/** The application whose roles the list is showing ('' / '0' = global scope). */
function selectedApplicationId(): string {
    return value('SelectedApplicationIdDropList');
}

/** Re-render the role list for whatever the Application picker currently holds. */
function reloadList(): Promise<void> {
    return ajaxSwap({
        container: 'RoleListContainer',
        url: `Role/List/${selectedApplicationId() || 0}`,
        spinnerInto: true,
        alertTitle: 'Error attempting to display the roles',
    });
}

export const List = {
    /**
     * Picker click -> swap Role/Detail into the detail pane.
     *
     * The legacy handler cleared the selection with
     * `$('.list-tile').removeClass('selected')` while adding it to the row
     * element -- two different nodes, so every click left another row
     * highlighted. `.list-tile` was doubly dead: it only rendered under
     * ViewStyle == "tile", and no caller ever passed a viewStyle at all.
     *
     * It also hand-triggered the Membership tab on success. Detail's strip
     * carries data-mcp-autotab now, so it opens its own first tab however the
     * pane arrived -- server-rendered on first paint or swapped in here.
     */
    Select(this: Element, roleId: number | string): void {
        const id = String(roleId ?? this.getAttribute('data-id') ?? '');
        if (!id) return;
        const item = this.closest('.mcp-picker-item') ?? this;
        setDisplay(`RoleLoading${id}`, true);
        void ajaxSwap({
            container: 'RoleDetailContainer',
            url: `Role/Detail/${id}`,
            spinnerInto: true,
            alertTitle: 'Detail Action failed to execute',
            onSuccess: () => {
                for (const other of all('#RoleListContainer .mcp-picker-item')) other.classList.remove('selected');
                item.classList.add('selected');
            },
            onComplete: () => setDisplay(`RoleLoading${id}`, false),
        });
    },

    /** Application picker -> reload the list (a select dispatches on change). */
    ApplicationChanged(): void {
        void reloadList();
    },

    DisplayCreateModal(): void {
        const domainId = value('CurrentDomainId');
        const applicationId = selectedApplicationId();
        void ajaxModal({
            // Role/Index.cshtml renders #RoleModal; the legacy handler named
            // #ApplicationDetailModal, which belongs to the Application detail
            // page, so the markup was written into an empty set and no dialog
            // ever appeared.
            modal: 'RoleModal',
            url: `Role/Create?domainId=${encodeURIComponent(domainId)}&applicationId=${encodeURIComponent(applicationId)}`,
            formId: 'CreateRoleForm',
            focusId: 'Name',
            errorTitle: 'New Role',
            errorMessage: 'New Role failed to execute:',
        });
    },
};

export const Create = {
    /**
     * POST the create form and swap the response into place: RoleController
     * returns this same form, cleared and carrying its success banner, or the
     * form with ModelState on failure.
     */
    SaveClick(): false {
        const form = one('CreateRoleForm') as HTMLFormElement | null;
        if (!form || !isValid(form)) return false;
        void ajaxSwap({
            container: 'RoleCreateContainer',
            url: 'Role/Create',
            method: 'POST',
            data: serialize(form),
            mode: 'replaceWith',
            busy: '#RoleCreateBusy',
            alertInto: 'RoleCreateMessageContainer',
            alertTitle: 'Failed to create the role',
            onSuccess: () => {
                // The server renders #RoleCreateSaveAlert only after a role was
                // actually written; a validation re-render must not reload the
                // list behind the modal (and must not claim success).
                if (!one('RoleCreateSaveAlert')) return;
                toast('success', 'Role created');
                void reloadList();
            },
        });
        return false;
    },

    /**
     * Domain picker on the New Role form -> refill the Application select.
     *
     * The endpoint answers either an array of applications or
     * `{ status: 'Error', message }` -- not the { success, modelState }
     * envelope -- so the response shape is handled here rather than through
     * ajaxJson. Properties are camelCase: the Web registers no
     * AddJsonOptions, so MVC serializes with the Web defaults.
     */
    DomainChanged(): void {
        void fillApplications();
    },
};

async function fillApplications(): Promise<void> {
    const select = document.getElementById('Role_Create_ApplicationId') as HTMLSelectElement | null;
    if (!select) return;
    const domainId = value('Role_Create_DomainId');
    // <Global> selected (or nothing yet): a global role binds to no
    // application. Empty the list rather than requesting a route that cannot
    // match an empty id.
    if (!domainId) {
        select.replaceChildren();
        select.value = '';
        return;
    }
    try {
        const result = await request(`Application/Domain/${encodeURIComponent(domainId)}/Options`, { accept: 'json' });
        const data = result.json as { status?: string; message?: string } | { resourceId: number; name: string }[] | null;
        if (!Array.isArray(data)) {
            toast('danger', 'Failed to retrieve the application list', data?.message ?? '');
            return;
        }
        select.replaceChildren(...data.map(app => new Option(app.name, String(app.resourceId))));
        // The legacy version selected the FIRST application in the list, which is
        // what the server does when it defaults the form, so keep that.
        select.value = data[0] ? String(data[0].resourceId) : '';
    } catch (error) {
        toast('danger', 'Failed to retrieve the application list', error instanceof Error ? error.message : String(error));
    }
}

export const Edit = {
    /**
     * Swap the Role Detail modal body from view-mode (Membership/Privileges
     * tabs) to edit-mode (the streamlined ApplicationRole/EditForm partial).
     * Wired to the pencil on ApplicationRole/Detail.cshtml.
     */
    Show(roleId: number | string, targetId: string, msgTargetId: string): void {
        const button = one('RoleDetailEditButton') as HTMLButtonElement | null;
        if (button) button.disabled = true;
        void ajaxSwap({
            container: targetId,
            url: `ApplicationRole/${roleId}/Edit`,
            alertInto: msgTargetId,
            alertTitle: 'Failed to render the role edit form',
            onComplete: () => {
                if (button) button.disabled = false;
            },
        });
    },

    /**
     * Copy the role the detail pane is showing.
     *
     * The legacy handler read #RoleIdField -- an id rendered only by the
     * standalone Role/Edit.cshtml, which nothing navigated to and which is
     * gone -- so on the one screen this button appears on it always asked for
     * `Role/Copy/undefined` and the dialog came back empty. Detail.cshtml
     * renders the id as #Id.
     */
    Copy(): void {
        const roleId = selectedRoleId();
        if (!roleId) return;
        void ajaxModal({
            modal: 'RoleModal',
            url: `Role/Copy/${roleId}`,
            formId: 'CreateRoleForm',
            focusId: 'Name',
            errorTitle: 'Copy Role',
            errorMessage: 'Failed to execute Copy Role function:',
        });
    },

    /**
     * Delete the role the detail pane is showing.
     *
     * On success the legacy handler submitted #ApplicationSelectionForm, a
     * form no rendered page has ever contained, so the deleted role stayed in
     * the list until the operator reloaded by hand.
     */
    Delete(): void {
        const roleId = selectedRoleId();
        if (!roleId) return;
        void confirm({
            title: 'Delete Role',
            message: 'Delete this role?',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Role/${roleId}`,
                    method: 'DELETE',
                    alertInto: 'RoleDetailMessageContainer',
                    errorTitle: 'An error occurred attempting to delete the role.',
                    onSuccess: () => {
                        hideModal('RoleModal');
                        toast('success', 'Role deleted');
                        const pane = one('RoleDetailContainer');
                        if (pane) pane.replaceChildren();
                        void reloadList();
                    },
                });
            },
        });
    },

    /**
     * Save handler for the in-modal RoleEditForm (ApplicationRole/EditForm).
     * PUTs to ApplicationRole/{roleId}; on success re-opens the role's detail
     * so the modal shows what was saved.
     */
    SaveClick(): void {
        const form = one('RoleEditForm') as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        const roleId = value('RoleEditId');
        const save = form.querySelector('button.btn-primary') as HTMLButtonElement | null;
        if (save) save.disabled = true;
        void ajaxJson({
            url: `ApplicationRole/${roleId}`,
            method: 'PUT',
            data: serialize(form),
            alertInto: 'ApplicationRoleDetailMessageContainer',
            errorTitle: 'Failed to save role changes',
            onSuccess: () => {
                toast('success', 'Role changes saved');
                ApplicationRole.Detail(roleId);
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (!modelState) return false;
                setValidationModelState(modelState);
                return true;
            },
            onComplete: () => {
                if (save) save.disabled = false;
            },
        });
    },
};
